import React, { useState, useEffect, useRef, memo } from 'react'
import { Box, Typography, TextField, IconButton, Chip } from '@mui/material'
import { Edit, Check, Close } from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { TranscriptSegment as TranscriptSegmentType, Word } from './types'
import { generateWordDiffs } from './utils'

interface Props {
  segment: TranscriptSegmentType
  isEditing: boolean
  isEditingSpeaker: boolean
  currentWordLocation: { segmentId: string; wordIndex: number } | null
  onToggleEdit: (segmentId: string) => void
  onToggleSpeakerEdit: (segmentId: string) => void
  onUpdateText: (segmentId: string, text: string) => void
  onUpdateSpeaker: (segmentId: string, oldName: string, newName: string) => void
  onSeek?: (time: number) => void
  onOpenAddVocabulary: (word: string) => void
}

const WordSpan = styled('span')<{
  $isActive: boolean
  $diffType?: 'added' | 'removed' | 'unchanged'
}>(({ $isActive, $diffType, theme }) => ({
  padding: $isActive ? '2px 4px' : '0',
  borderRadius: $isActive ? '4px' : '0',
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  textDecoration: $diffType === 'removed' ? 'line-through' : 'none',
  backgroundColor:
    $diffType === 'added'
      ? theme.palette.secondary.light
      : $diffType === 'removed'
        ? theme.palette.secondary.light
        : $isActive
          ? theme.palette.primary.light
          : 'transparent',
  color:
    $diffType === 'added' || $diffType === 'removed'
      ? theme.palette.secondary.contrastText
      : $isActive
        ? theme.palette.primary.contrastText
        : 'inherit',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

const DiffWordSpan = styled('span')<{ $diffType: 'added' | 'removed' }>(
  ({ $diffType, theme }) => ({
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.contrastText,
    padding: '2px 4px',
    borderRadius: '4px',
    textDecoration: $diffType === 'removed' ? 'line-through' : 'none',
    fontWeight: 500,
    cursor: $diffType === 'added' ? 'pointer' : 'default',
    '&:hover': {
      backgroundColor:
        $diffType === 'added' ? theme.palette.secondary.main : undefined,
    },
  }),
)

const SegmentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

const EditableChip = styled(Chip)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

const WordComponent = memo<{
  word: Word
  isActive: boolean
  diffType?: 'added' | 'removed' | 'unchanged'
  onSeek?: (time: number) => void
}>(({ word, isActive, diffType, onSeek }) => (
  <WordSpan
    $isActive={isActive}
    $diffType={diffType}
    onClick={() => onSeek?.(word.startTime)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSeek?.(word.startTime)
      }
    }}
    role="button"
    tabIndex={0}
    aria-label={`Seek video to "${word.content}"`}
  >
    {word.content}
  </WordSpan>
))

const DiffWordComponent = memo<{
  word: string
  diffType: 'added' | 'removed'
  onClick?: () => void
}>(({ word, diffType, onClick }) => (
  <DiffWordSpan
    $diffType={diffType}
    onClick={onClick}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onClick()
            }
          }
        : undefined
    }
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    aria-label={onClick ? `Add "${word}" to vocabulary` : undefined}
  >
    {word}
  </DiffWordSpan>
))

export const TranscriptSegment = memo<Props>(
  ({
    segment,
    isEditing,
    isEditingSpeaker,
    currentWordLocation,
    onToggleEdit,
    onToggleSpeakerEdit,
    onUpdateText,
    onUpdateSpeaker,
    onSeek,
    onOpenAddVocabulary,
  }) => {
    let [editText, setEditText] = useState(segment.text)
    let [editSpeaker, setEditSpeaker] = useState(segment.speaker)
    let textFieldRef = useRef<HTMLTextAreaElement>(null)
    let speakerFieldRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      setEditText(segment.text)
    }, [segment.text])

    useEffect(() => {
      setEditSpeaker(segment.speaker)
    }, [segment.speaker])

    let handleSave = () => {
      onUpdateText(segment.id, editText)
      onToggleEdit(segment.id)
    }

    let handleCancel = () => {
      setEditText(segment.text)
      onToggleEdit(segment.id)
    }

    let handleSaveSpeaker = () => {
      if (editSpeaker !== segment.speaker) {
        onUpdateSpeaker(segment.id, segment.speaker, editSpeaker)
      }
      onToggleSpeakerEdit(segment.id)
    }

    let handleCancelSpeaker = () => {
      setEditSpeaker(segment.speaker)
      onToggleSpeakerEdit(segment.id)
    }

    let handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && e.ctrlKey) {
        handleSave()
      } else if (e.key === 'Escape') {
        handleCancel()
      }
    }

    let handleSpeakerKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSaveSpeaker()
      } else if (e.key === 'Escape') {
        handleCancelSpeaker()
      }
    }

    let formatTime = (seconds: number) => {
      let mins = Math.floor(seconds / 60)
      let secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    let handleWordClick = (word: string) => {
      onOpenAddVocabulary(word)
    }

    return (
      <SegmentContainer onClick={(e) => e.stopPropagation()}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isEditingSpeaker ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  inputRef={speakerFieldRef}
                  size="small"
                  value={editSpeaker}
                  onChange={(e) => setEditSpeaker(e.target.value)}
                  onKeyDown={handleSpeakerKeyDown}
                  autoFocus
                  variant="standard"
                  sx={{ width: '100px' }}
                />
                <IconButton
                  size="small"
                  onClick={handleSaveSpeaker}
                  aria-label="Save speaker name"
                >
                  <Check fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCancelSpeaker}
                  aria-label="Cancel editing speaker name"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <EditableChip
                label={segment.speaker}
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => onToggleSpeakerEdit(segment.id)}
                aria-label={`Edit speaker name: ${segment.speaker}`}
              />
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ cursor: 'pointer' }}
              onClick={() => onSeek?.(segment.startTime)}
            >
              {formatTime(segment.startTime)} - {formatTime(segment.endTime)}
            </Typography>
          </Box>
          {!isEditing && !isEditingSpeaker && (
            <IconButton
              size="small"
              onClick={() => onToggleEdit(segment.id)}
              aria-label="Edit segment text"
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>
        {isEditing ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <TextField
              inputRef={textFieldRef}
              fullWidth
              multiline
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              autoFocus
              helperText="Ctrl+Enter to save, Esc to cancel"
            />
            <IconButton
              size="small"
              onClick={handleSave}
              color="primary"
              aria-label="Save segment text"
            >
              <Check />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleCancel}
              aria-label="Cancel editing segment text"
            >
              <Close />
            </IconButton>
          </Box>
        ) : (
          <Typography variant="body1" component="div">
            {segment.words.length > 0 ? (
              // Check if we have changes to show diffs
              // Show diff highlighting
              segment.originalText && segment.text !== segment.originalText ? ( // Show normal word highlighting
                generateWordDiffs(segment.originalText, segment.text).map(
                  (diff, index) => (
                    <React.Fragment key={`diff-${index}`}>
                      {diff.type === 'unchanged' ? (
                        <span>{diff.word}</span>
                      ) : (
                        <DiffWordComponent
                          word={diff.word}
                          diffType={diff.type}
                          onClick={
                            diff.type === 'added'
                              ? () => handleWordClick(diff.word)
                              : undefined
                          }
                        />
                      )}
                      {index <
                        generateWordDiffs(
                          segment?.originalText ?? '',
                          segment.text,
                        ).length -
                          1 && ' '}
                    </React.Fragment>
                  ),
                )
              ) : (
                segment.words.map((word, index) => {
                  let isActive =
                    currentWordLocation?.segmentId === segment.id &&
                    currentWordLocation?.wordIndex === index

                  return (
                    <React.Fragment key={word.id}>
                      <WordComponent
                        word={word}
                        isActive={isActive}
                        onSeek={onSeek}
                      />
                      {index < segment.words.length - 1 && ' '}
                    </React.Fragment>
                  )
                })
              )
            ) : // No words, show text with diff if available
            segment.originalText && segment.text !== segment.originalText ? (
              generateWordDiffs(segment.originalText, segment.text).map(
                (diff, index) => (
                  <React.Fragment key={`diff-${index}`}>
                    {diff.type === 'unchanged' ? (
                      <span>{diff.word}</span>
                    ) : (
                      <DiffWordComponent
                        word={diff.word}
                        diffType={diff.type}
                        onClick={
                          diff.type === 'added'
                            ? () => handleWordClick(diff.word)
                            : undefined
                        }
                      />
                    )}
                    {index <
                      generateWordDiffs(
                        segment?.originalText ?? '',
                        segment.text,
                      ).length -
                        1 && ' '}
                  </React.Fragment>
                ),
              )
            ) : (
              <span>{segment.text}</span>
            )}
          </Typography>
        )}
      </SegmentContainer>
    )
  },
)
